import {Injectable} from '@angular/core';
import {Router, ActivatedRouteSnapshot, RouterStateSnapshot, CanActivateChild} from '@angular/router';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import * as moment from 'moment';

import {ServerResponseData, HttpStatusCodesEnum, AuthApiService} from '@spa/api';

import {Helper} from '../../../shared/classes';
import {PanelPaths} from '../../../shared/paths';
import {CacheCollectionService} from '../../../shared/services';

import {PermissionRulesTrait} from '../traits';

import {CommonMessages} from '../../../../assets/messages/common.messages';

import {AccountTypeSelector, ErrorMessageIdSelector} from '../../../../assets/descriptors';

@Injectable()
export class AccountsAccessGuardService implements CanActivateChild {
    constructor(
        private authenticationService: AuthApiService,
        protected router: Router,
        protected cacheCollectionService: CacheCollectionService
    ) {
    }

    private navigateToErrorPage(message: string): void {
        const timestamp = moment().unix();

        this.cacheCollectionService.addItem(timestamp.toString(), message);

        this.router.navigate(['/error'], {
            queryParams: {
                [ErrorMessageIdSelector]: timestamp
            }
        }).finally();
    }

    private doCheck(route: ActivatedRouteSnapshot): any {
        return this.authenticationService.getAuthenticatedUserAllPermissionsAsync().pipe(
            map((response: ServerResponseData<boolean>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    let message = CommonMessages.UnknownError.message;

                    switch (response.status) {
                        case HttpStatusCodesEnum.Unauthorized:
                            message = CommonMessages.UnauthorizedError.message;
                            break;
                        case HttpStatusCodesEnum.Forbidden:
                            message = CommonMessages.ForbiddenError.message;
                            break;
                        case HttpStatusCodesEnum.InternalServerError:
                            message = CommonMessages.InternalServerError.message;
                            break;
                    }

                    this.navigateToErrorPage(message);

                    return false;
                }

                const authUserPermissions = this.authenticationService.getAuthenticatedUser().permissions;

                return this.checkUserAccessByAccountType(route, authUserPermissions);
            })
        );
    }

    private checkUserAccessByAccountType(route: ActivatedRouteSnapshot, authUserPermissions: string[]): boolean {
        let childViewPermissions: string[] = [];

        const accountType = parseInt(route.parent.paramMap.get(AccountTypeSelector), 10);

        let path = route.routeConfig.path;
        if (!path) {
            path = route.parent.routeConfig.path;
        }
        switch (path) {
            case PanelPaths.children.accounts.children.edit.children.details.path:
            case PanelPaths.children.accounts.children.edit.children.orderRoutes.path:
            case PanelPaths.children.accounts.children.edit.children.riskSettings.path:
            case PanelPaths.children.accounts.children.edit.children.plans.path:
            case PanelPaths.children.accounts.children.edit.children.plans.children.details.path:
            case PanelPaths.children.accounts.children.edit.children.plans.children.stats.path:
            case PanelPaths.children.accounts.children.edit.children.plans.children.trades.path:
                childViewPermissions = Helper.enumsHelper().permissionsEnumHelper()
                    .getDetailsViewPermissionsByAccountType(accountType);
                break;
            case PanelPaths.children.accounts.children.edit.children.feePlans.path:
                childViewPermissions = Helper.enumsHelper().permissionsEnumHelper()
                    .getFeePlansViewPermissions(accountType);
                break;
            case PanelPaths.children.accounts.children.edit.children.currentPosition.path:
                childViewPermissions = Helper.enumsHelper().permissionsEnumHelper()
                    .getCurrentPositionsViewPermissions();
                break;
            case PanelPaths.children.accounts.children.edit.children.users.path:
                childViewPermissions = PermissionRulesTrait.getViewClientUsersPermissionRule().permissions;
                break;
        }

        if (!Helper.getUnionStrategyPermissionsCheck(childViewPermissions, authUserPermissions)) {
            this.navigateToErrorPage(CommonMessages.ForbiddenError.message);

            return false;
        }

        return true;
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.doCheck(route);
    }
}
